import { useEffect, useState } from 'react';
import { Save, Eye, EyeOff, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardContent, Input } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { FileImageUploader } from '@/components/shared/FileImageUploader';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('festival');

// Single-vendor admin doesn't need per-banner color knobs — the storefront
// uses the brand palette (accent #D95100 over ink #0A1D04). Color fields are
// retained on the type only for backwards compatibility with old JSON.
interface FestivalContent {
  enabled: boolean;
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
  image: string;
  // Legacy color overrides — preserved as-is when saving; not exposed in UI.
  bgColor?: string;
  textColor?: string;
  ctaBgColor?: string;
  ctaTextColor?: string;
}

const DEFAULTS: FestivalContent = {
  enabled: true,
  eyebrow: 'Limited time offer',
  title: 'Dashain & Tihar Sale 🪔',
  description: 'Up to 50% off on selected products. Celebrate the festivals with the best deals!',
  ctaLabel: 'Shop the Sale',
  ctaHref: '/offers/festival-sale',
  image: '',
};

const PREVIEW_BG = '#0A1D04';
const PREVIEW_ACCENT = '#D95100';
const PREVIEW_INK = '#EBEAE1';

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-700 block mb-1.5">{label}</span>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </label>
  );
}

export function FestivalCms() {
  const [content, setContent] = useState<FestivalContent>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) setContent({ ...DEFAULTS, ...(await res.json()) });
      } catch {
        toast.error('Could not reach storefront CMS.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = <K extends keyof FestivalContent>(key: K, val: FestivalContent[K]) =>
    setContent((c) => ({ ...c, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Festival banner saved', { description: 'Live on the storefront in a moment.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-5 max-w-3xl">
      <PageHeader
        title="Festival Banner"
        description="Wide promotional banner shown between sections on the homepage."
        actions={
          <div className="flex items-center gap-2">
            {STOREFRONT_URL && (
              <Button asChild variant="outline" size="sm">
                <a href={STOREFRONT_URL} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> Open storefront
                </a>
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        }
      />

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading banner…
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Visibility toggle */}
          <Card>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary-50 text-primary-700 flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {content.enabled ? 'Banner is live on the storefront' : 'Banner is hidden'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {content.enabled
                      ? 'Customers see this between featured products and stores.'
                      : 'Toggle on to show it again.'}
                  </p>
                </div>
              </div>
              <Button
                variant={content.enabled ? 'outline' : 'default'}
                size="sm"
                onClick={() => set('enabled', !content.enabled)}
              >
                {content.enabled ? (
                  <>
                    <EyeOff className="h-3.5 w-3.5" /> Hide
                  </>
                ) : (
                  <>
                    <Eye className="h-3.5 w-3.5" /> Show
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardContent className="p-4">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Preview
              </p>
              <div
                className="relative rounded-2xl py-10 px-8 overflow-hidden text-center"
                style={{ backgroundColor: PREVIEW_BG, color: PREVIEW_INK }}
              >
                {content.image && (
                  <img
                    src={content.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
                  />
                )}
                <div className="relative">
                  {content.eyebrow && (
                    <p
                      className="text-xs font-semibold uppercase tracking-wider mb-2"
                      style={{ color: PREVIEW_ACCENT }}
                    >
                      {content.eyebrow}
                    </p>
                  )}
                  <h3 className="text-2xl font-bold mb-2">{content.title || 'Add a title'}</h3>
                  <p className="opacity-90 mb-5 max-w-xl mx-auto text-sm">
                    {content.description || 'Add a short description.'}
                  </p>
                  {content.ctaLabel && (
                    <span
                      className="inline-block px-6 py-2.5 rounded-xl font-semibold text-sm"
                      style={{ backgroundColor: PREVIEW_ACCENT, color: '#FFFFFF' }}
                    >
                      {content.ctaLabel}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fields */}
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Eyebrow" hint="Tiny label above the title.">
                  <Input
                    value={content.eyebrow}
                    onChange={(e) => set('eyebrow', e.target.value)}
                    placeholder="Limited time offer"
                  />
                </Field>
                <Field label="Title">
                  <Input
                    value={content.title}
                    onChange={(e) => set('title', e.target.value)}
                    placeholder="Dashain & Tihar Sale"
                  />
                </Field>
              </div>

              <Field label="Description">
                <textarea
                  value={content.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={3}
                  placeholder="A short tagline shoppers will see."
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Button label">
                  <Input
                    value={content.ctaLabel}
                    onChange={(e) => set('ctaLabel', e.target.value)}
                    placeholder="Shop the Sale"
                  />
                </Field>
                <Field label="Button link">
                  <Input
                    value={content.ctaHref}
                    onChange={(e) => set('ctaHref', e.target.value)}
                    placeholder="/offers/festival-sale"
                    className="font-mono text-xs"
                  />
                </Field>
              </div>

              <FileImageUploader
                label="Background image (optional)"
                value={content.image}
                onChange={(v) => set('image', v)}
                aspect="aspect-[3/1]"
                helperText="Shown subtly behind the text. Wide 3:1 aspect works best."
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
